import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { CircleCheck, FilePlus2, Inbox, RefreshCw } from "lucide-react";
import { Badge } from "../../Franchise/D2CDashboard/ui/badge";
import { Button } from "../../Franchise/D2CDashboard/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "../../Franchise/D2CDashboard/ui/card";
import { Callout } from "../../Franchise/D2CDashboard/ui/misc";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../Franchise/D2CDashboard/ui/table";
import { EmptyState, ErrorState, SkeletonRows } from "../../Franchise/D2CDashboard/StateBlock";
import { WARRANTY_PERMISSION, hasWarrantyPermission } from "../common/warrantyAccess";
import { listPendingVendorPolicies } from "../common/warrantyApi";
import { usePagedList } from "../common/usePagedList";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { formatDate } from "../common/format";
import { NoWarrantyAccess, PageHeader, Shell } from "../AllWarranties/Shell";
import AddVendorPolicyDialog from "./AddVendorPolicyDialog";
import { coverWindow, customerLabel, daysWaitingLabel, planLabel } from "./vendorPolicy";

// A cover waiting a week or more is shown in amber: the rider has paid and has no theft or roadside cover yet.
const LONG_WAIT_DAYS = 7;

const PendingRow = ({ item, onAdd }) => (
  <TableRow>
    <TableCell className="tw-font-mono tw-text-xs tw-font-medium">{item.frameNumber || "-"}</TableCell>
    <TableCell className="tw-max-w-[200px] tw-truncate" title={item.productName || undefined}>
      {item.productName || "-"}
    </TableCell>
    <TableCell>{planLabel(item) || "-"}</TableCell>
    <TableCell className="tw-whitespace-nowrap tw-text-muted-foreground">{formatDate(item.paidOn)}</TableCell>
    <TableCell className="tw-whitespace-nowrap">{coverWindow(item)}</TableCell>
    <TableCell className="tw-whitespace-nowrap">
      {Number.isInteger(item.daysWaiting) && item.daysWaiting >= LONG_WAIT_DAYS ? (
        <Badge tone="warning">{daysWaitingLabel(item.daysWaiting)}</Badge>
      ) : (
        daysWaitingLabel(item.daysWaiting)
      )}
    </TableCell>
    <TableCell className="tw-whitespace-nowrap">{customerLabel(item)}</TableCell>
    <TableCell className="tw-text-right">
      <Button
        size="sm"
        variant="outline"
        aria-label={`Add vendor policy for ${item.frameNumber || item.subscriptionId}`}
        onClick={() => onAdd(item)}
      >
        <FilePlus2 aria-hidden="true" /> Add vendor policy
      </Button>
    </TableCell>
  </TableRow>
);

// Theft & roadside to buy: the paid Assure+ covers waiting for a vendor policy, oldest first. There
// is no insurer API, so ops buy one policy per subscription from the vendor and add it here; the
// warranty service then marks the covers active and sends the PDF to the rider.
const VendorPolicies = () => {
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.OPS);
  useWarrantyNav(allowed);

  const fetchPage = useCallback((cursor) => listPendingVendorPolicies(accessToken, cursor), [accessToken]);
  const list = usePagedList(fetchPage, allowed);
  const [adding, setAdding] = useState(null);
  const [notice, setNotice] = useState(null);

  const done = (item, notification) => {
    setAdding(null);
    list.removeWhere((row) => row.subscriptionId === item.subscriptionId);
    setNotice(notification);
  };

  const saved = (item, data) =>
    done(item, {
      tone: "success",
      text: `Vendor policy ${data?.policyNumber || ""} saved for ${item.frameNumber || item.subscriptionId}. Theft and roadside cover is active, and the rider is being sent the PDF.`,
    });

  const alreadyIssued = (item) =>
    done(item, {
      tone: "warning",
      text: `A vendor policy was already added for ${item.frameNumber || item.subscriptionId}, so it has been taken off this list.`,
    });

  if (!allowed) {
    return (
      <Shell>
        <NoWarrantyAccess>
          Theft &amp; roadside to buy is open to super admins and users with the Warranty Operations permission.
        </NoWarrantyAccess>
      </Shell>
    );
  }

  return (
    <Shell>
      <PageHeader
        title="Theft & roadside to buy"
        sub="Paid Assure+ covers waiting for their vendor policy, oldest first. Market IN. Buy one policy per subscription from the vendor, then add it here."
        actions={
          <Button
            variant="outline"
            size="icon"
            onClick={() => list.reload(false)}
            disabled={list.status === "loading"}
            aria-label="Reload the list"
            title="Reload the list"
          >
            <RefreshCw aria-hidden="true" />
          </Button>
        }
      />

      {notice && (
        <Callout tone={notice.tone} icon={CircleCheck} role="status" className="tw-mb-4">
          {notice.text}
        </Callout>
      )}

      <Card>
        <CardHeader className="tw-items-center">
          <div>
            <CardTitle>Waiting for a vendor policy</CardTitle>
            <CardDescription>The longest wait first.</CardDescription>
          </div>
        </CardHeader>
        {list.status === "loading" && <SkeletonRows />}
        {list.status === "error" && (
          <div className="tw-p-4 tw-pt-0">
            <ErrorState message={list.message} onRetry={() => list.reload(false)} />
          </div>
        )}
        {list.status === "ready" && list.items.length === 0 && (
          <EmptyState icon={Inbox} title="Nothing to buy">
            Every paid theft and roadside cover has its vendor policy.
          </EmptyState>
        )}
        {list.status === "ready" && list.items.length > 0 && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Frame</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Paid on</TableHead>
                  <TableHead>Cover window</TableHead>
                  <TableHead>Days waiting</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>
                    <span className="tw-sr-only">Action</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.items.map((item) => (
                  <PendingRow key={item.subscriptionId} item={item} onAdd={setAdding} />
                ))}
              </TableBody>
            </Table>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3 tw-p-4">
              {list.moreError && (
                <span role="alert" className="tw-text-xs tw-text-red-700">
                  {list.moreError}
                </span>
              )}
              {list.nextCursor ? (
                <Button variant="outline" onClick={list.loadMore} disabled={list.loadingMore}>
                  {list.loadingMore ? "Loading" : "Load more"}
                </Button>
              ) : (
                <span className="tw-text-xs tw-text-muted-foreground">
                  {list.items.length.toLocaleString("en-IN")} shown. That is all.
                </span>
              )}
            </div>
          </>
        )}
      </Card>

      <AddVendorPolicyDialog
        item={adding}
        accessToken={accessToken}
        onClose={() => setAdding(null)}
        onSaved={saved}
        onAlreadyIssued={alreadyIssued}
      />
    </Shell>
  );
};

export default VendorPolicies;
